import { Controller } from "@hotwired/stimulus"
import { EnhancedSelect } from "../utils/enhanced_select"

export default class ColumnMappingController extends Controller {
  static targets = ["fieldSelect"]

  declare fieldSelectTargets: HTMLSelectElement[]

  private enhancedSelects: Map<HTMLSelectElement, EnhancedSelect> = new Map()

  private static STANDARD_FIELDS = [
    "first_name",
    "last_name",
    "email",
    "cell_phone",
    "voice_phone",
    "ref_no",
  ]

  connect(): void {
    this.initializeSelects()
  }

  disconnect(): void {
    this.enhancedSelects.forEach((es) => es.destroy())
    this.enhancedSelects.clear()
  }

  private initializeSelects(): void {
    this.fieldSelectTargets.forEach((select) => {
      const enhanced = new EnhancedSelect(select, {
        placeholder: "Select or type to create new...",
        allowClear: true,
        create: (input: string) => {
          const trimmed = input.trim()
          if (!trimmed) return false
          return {
            value: `new_custom_field:${trimmed}`,
            text: `${trimmed} (new)`,
          }
        },
        createFilter: (input: string) => {
          const normalized = input.toLowerCase().replace(/\s+/g, "_")
          return !ColumnMappingController.STANDARD_FIELDS.includes(normalized)
        },
        synchronousRefresh: true,
        onChange: (value: string | string[]) => this.handleChange(select, value as string),
        render: {
          option_create: (data: { input: string }, escape: (str: string) => string) => {
            return `<div class="create">Create new field: <strong>${escape(data.input)}</strong></div>`
          },
          no_results: () => {
            return '<div class="no-results">Type to create a new custom field...</div>'
          },
        },
      })
      this.enhancedSelects.set(select, enhanced)
    })
    this.updateDisabledOptions()
  }

  private handleChange(select: HTMLSelectElement, value: string): void {
    if (value === "__create_new__") {
      const enhanced = this.enhancedSelects.get(select)
      if (enhanced) {
        enhanced.clear(true)
        enhanced.open()
        enhanced.focus()
        setTimeout(() => this.injectPrompt(select), 10)
      }
      return
    }
    this.updateDisabledOptions()
  }

  private injectPrompt(select: HTMLSelectElement): void {
    const enhanced = this.enhancedSelects.get(select)
    if (!enhanced) return

    const dropdownContent = enhanced.getDropdownContent()
    if (!dropdownContent.querySelector(".create-mode-prompt")) {
      const prompt = document.createElement("div")
      prompt.className = "no-results create-mode-prompt"
      prompt.textContent = "Type to create a new custom field..."
      dropdownContent.insertBefore(prompt, dropdownContent.firstChild)
    }
    enhanced.clearActiveOption()
    dropdownContent.scrollTop = 0
  }

  private updateDisabledOptions(): void {
    const selectedFields = new Set<string>()
    this.fieldSelectTargets.forEach((select) => {
      const enhanced = this.enhancedSelects.get(select)
      if (!enhanced) return

      const value = enhanced.getValue() as string
      if (value && ColumnMappingController.STANDARD_FIELDS.includes(value)) {
        selectedFields.add(value)
      }
    })

    this.fieldSelectTargets.forEach((select) => {
      const enhanced = this.enhancedSelects.get(select)
      if (!enhanced) return

      const currentValue = enhanced.getValue() as string
      let needsSync = false

      ColumnMappingController.STANDARD_FIELDS.forEach((field) => {
        const option = select.querySelector(
          `option[value="${field}"]`
        ) as HTMLOptionElement | null

        if (option) {
          const shouldDisable = selectedFields.has(field) && currentValue !== field
          if (option.disabled !== shouldDisable) {
            option.disabled = shouldDisable
            needsSync = true
          }
        }
      })

      if (needsSync) {
        enhanced.sync()
      }
    })
  }
}
